<script lang="ts" setup>
import IMonetize from '~/assets/images/mighty-networks-alternative/monetize-icon.svg?component'
import IMonetizeYourContent from '~/assets/images/mighty-networks-alternative/monetize-your-content.svg?component'

import JoinTheRevolution from '~/components/reusable/JoinTheRevolution.vue'
import LaunchWebApp from '~/components/reusable/LaunchWebApp.vue'
import StartBuilding from '~/components/reusable/StartBuilding.vue'

const { t } = useI18n()
const localeRoute = useLocaleRoute()

const {
  public: { siteUrl },
} = useRuntimeConfig()

definePageMeta({
  layout: 'menu',
})

useSeoMeta({
  title: t('monetize-your-community-with', { n: 'Belong' }),
  description: t('explore-belongs-innovative-approach'),
  ogTitle: t('monetize-your-community-with', { n: 'Belong' }),
  ogDescription: t('explore-belongs-innovative-approach'),
  ogImage: `${siteUrl}/images/mighty-networks-alternative/snippet_screen.png`,
  ogImageWidth: '1200',
  ogImageHeight: '630',
  ogImageType: 'image/png',
  ogType: 'website',
  twitterTitle: t('monetize-your-community-with', { n: 'Belong' }),
  twitterDescription: t('explore-belongs-innovative-approach'),
  twitterImage: `${siteUrl}/images/mighty-networks-alternative/snippet_screen.png`,
  twitterCard: 'summary_large_image',
})
</script>

<template>
  <section>
    <div
      class="monetize-your-community min-[1440px]:pl-0 pt-18 xl:flex xl:flex-row-reverse xl:justify-between xl:max-w-316.5 xl:mx-auto xl:pl-15"
    >
      <NuxtImg
        format="webp"
        :alt="t('monetize-your-community-with')"
        class="h-138.75 w-full max-w-129 mx-auto object-cover xl:mx-0 xl:px-0 xl:max-w-171.5 xl:h-174.5 xl:object-left"
        src="/images/mighty-networks-alternative/monetize-your-community.png"
      />

      <div
        class="flex flex-col justify-center max-w-104 mx-auto px-5 xl:mx-0 xl:max-w-150 xl:w-150 xl:justify-start xl:mt-56.5 xl:relative xl:px-0"
      >
        <h1
          class="text-[2.75rem] font-light tracking-wide leading-[3rem] whitespace-pre-line break-words xl:text-[4.25rem] xl:leading-[4.625rem]"
        >
          {{ t('monetize-your-community-with') }}
          <p class="flex flex-row items-center text-[#7540EE]">
            {{ t('belong') }}
            <IMonetize class="ml-3.5" />
          </p>
        </h1>
      </div>
    </div>
  </section>

  <section>
    <div
      class="flex flex-col items-center mt-13 lg:flex-row lg:items-start lg:justify-between lg:mx-auto lg:px-5 lg:max-w-282.5 lg:mt-20 xl:px-0"
    >
      <div
        class="flex justify-center px-9 lg:px-0 lg:mt-6.5 lg:object-cover lg:min-w-107.5 lg:w-107.5 lg:h-58"
      >
        <NuxtImg
          format="webp"
          :alt="t('double-the-revenue')"
          class="h-full max-h-47.5 w-full max-w-88.5 object-cover lg:max-w-full lg:max-h-full lg:object-top"
          src="/images/mighty-networks-alternative/double-the-revenue.png"
        />
      </div>
      <div
        class="max-w-98 text-center sm:max-w-full sm:px-5 lg:max-w-150 lg:text-left xl:px-0 xl:text-[1.75rem]"
      >
        <div
          class="text-2xl tracking-wide mt-6 max-w-80 mx-auto lg:max-w-full lg:mt-0"
        >
          <p class="text-[#7540EE]">
            {{ t('double-the-revenue') }}
            <span class="text-white">
              {{ t('streams-from-your-community') }}
            </span>
          </p>
        </div>
        <p class="text-lg tracking-wide mx-5 mt-5 sm:mx-0 lg:mt-10">
          {{ t('explore-belongs-innovative-approach') }}
        </p>
      </div>
    </div>
  </section>

  <section>
    <div
      class="mt-8 lg:flex lg:flex-row-reverse lg:justify-between lg:max-w-248 lg:mx-auto lg:mt-20.5"
    >
      <div
        class="gradients gradients-off w-76.75 h-59.5 overflow-hidden mx-auto mb-8 lg:h-130 lg:m-0"
      >
        <NuxtImg
          format="webp"
          :alt="t('selling-memberships')"
          class="w-76.75 h-59.5 object-cover lg:h-full lg:!object-center"
          style="object-position: 0 -6rem"
          src="/images/mighty-networks-alternative/selling-memberships.png"
        />
      </div>
      <div
        class="text-center lg:max-w-144.5 lg:text-left lg:flex lg:flex-col lg:pt-20"
      >
        <div class="text-2xl tracking-wide lg:text-[1.75rem]">
          <p class="max-w-70 mx-auto lg:mx-0 lg:max-w-full">
            <span class="text-[#7540EE]">
              {{ t('selling-memberships') }}
            </span>
            {{ t('unlock-new-revenue-potential') }}
          </p>
        </div>

        <div
          class="max-w-98 mx-auto px-5 text-lg tracking-wide mt-7.5 sm:max-w-full lg:px-0 lg:mt-10"
        >
          <p>
            {{
              t(
                'utilize-belongs-token-gating-with-nfts-for-exclusive-community-access-and-enjoy-our-transparent-pricing-model'
              )
            }}
            <span class="text-[#7540EE]"> {{ t('only-2-of-sales') }}</span>
            {{
              t(
                'our-diverse-payment-system-supports-apple-pay-google-pay-credit-card-payments-and-crypto-transactions'
              )
            }}
          </p>
          <p class="mt-5.5">
            {{
              t(
                'start-leveraging-belongs-diverse-payment-options-to-maximize-your-membership-revenue-now'
              )
            }}
          </p>
        </div>

        <div
          class="flex flex-row items-center justify-center mt-5 gap-3 flex-wrap mx-5 lg:justify-start lg:mt-8 lg:mx-0"
        >
          <ReusableInstallButtons />
          <LaunchWebApp />
        </div>
      </div>
    </div>
  </section>

  <section>
    <div
      class="mt-17.5 lg:flex lg:flex-row lg:justify-between lg:max-w-271.5 lg:mx-auto lg:px-5 lg:mt-28 xl:px-0"
    >
      <div class="relative lg:w-99 lg:flex lg:justify-center">
        <NuxtImg
          format="webp"
          :alt="t('monetizing-access-to-private-chats')"
          class="hidden w-75.5 h-127.5 object-cover lg:flex"
          src="/images/mighty-networks-alternative/monetizing-access-phone.png"
        />

        <NuxtImg
          format="webp"
          :alt="t('monetizing-access-to-private-chats')"
          class="w-71 h-59.5 object-cover mx-auto lg:absolute lg:top-18 lg:left-0 lg:right-0 lg:m-auto lg:w-99 lg:h-82.5"
          src="/images/mighty-networks-alternative/monetizing-access.png"
        />
      </div>
      <div class="text-center lg:w-144.5 lg:text-left lg:pt-14.5">
        <div
          class="text-2xl tracking-wide max-w-87.5 mx-auto px-5 mt-7 lg:px-0 lg:mx-0 lg:max-w-full lg:text-[1.75rem]"
        >
          <p>
            {{ t('monetizing-access-to-private-chats') }}
            <span class="text-[#7540EE]">{{ t('connect-and-earn') }}</span>
          </p>
        </div>

        <div
          class="max-w-98 mx-auto px-5 text-lg tracking-wide mt-7.5 sm:max-w-full lg:mx-0 lg:px-0 lg:mt-10"
        >
          <p>
            {{
              t(
                'belong-provides-access-to-paid-or-private-content-for-verified-community-members-this-feature-enables-cases-like-education-fan-communication-gated-live-streams-and-more-transforming-every-interaction-into-a-potential-revenue-stream'
              )
            }}
          </p>
          <p class="mt-5.5">
            {{
              t(
                'join-belong-and-start-monetizing-your-educational-content-fan-interactions-and-exclusive-live-streams'
              )
            }}
          </p>
        </div>

        <NuxtLink
          :to="localeRoute('paid-content-nft')"
          class="flex flex-row items-center justify-center gap-2.5 border border-[#515151] w-59 h-11 rounded-full mt-6.5 mx-auto text-sm tracking-wide lg:mx-0 lg:mt-8"
        >
          <IMonetizeYourContent />
          <p>{{ t('monetize-your-content') }}</p>
        </NuxtLink>
      </div>
    </div>
  </section>

  <section>
    <div
      class="mt-15 lg:flex lg:flex-row-reverse lg:justify-between lg:mx-auto lg:max-w-246.5 lg:mt-30"
    >
      <div
        class="gradients w-76.75 h-59.5 overflow-hidden mx-auto mb-8 lg:w-74.5 lg:h-127 lg:mx-0 lg:mb-0"
      >
        <NuxtImg
          format="webp"
          :alt="t('hosting-and-monetizing-events')"
          class="w-76.75 h-59.5 object-cover lg:!object-center lg:w-full lg:h-full"
          style="object-position: 0 -10.5rem"
          src="/images/mighty-networks-alternative/hosting-and-monetizing.png"
        />
      </div>
      <div class="text-center lg:max-w-144.5 lg:text-left">
        <div
          class="text-2xl tracking-wide max-w-87.5 px-5 mx-auto lg:max-w-full lg:px-0 lg:mt-14 lg:text-[1.75rem]"
        >
          <p>
            {{ t('hosting-and-monetizing-events') }}
            <span class="text-[#7540EE]">
              {{ t('transform-your-events-into-revenue-generators') }}
            </span>
          </p>
        </div>

        <div
          class="max-w-98 text-lg tracking-wide mx-auto px-5 mt-5 sm:max-w-full lg:px-0 lg:mt-11"
        >
          <p>
            {{
              t(
                'belongs-nft-ticketing-system-elevates-your-events-offering-instant-payouts-reduced-fees-and-turning-tickets-into-valuable-digital-collectibles'
              )
            }}
          </p>
          <p class="mt-4.5 lg:mt-6">
            {{
              t(
                'revolutionize-your-event-management-and-monetization-strategies-with-belongs-nft-ticketing'
              )
            }}
          </p>
        </div>

        <div class="px-5 mx-auto max-w-97 mt-9.5 lg:px-0 lg:mx-0">
          <NuxtLink
            :to="localeRoute('nft-tickets')"
            class="flex flex-row items-center justify-center h-12 text-white rounded-full border-2 font-semibold tracking-wide leading-normal bg-[#7540EE] border-[rgba(23,13,48,0.36)]"
          >
            <img
              alt=""
              src="~/assets/images/events/icon-nft-ticket.svg"
              class="mr-4 w-6.25 h-6.25"
            />
            {{ t('nft-tickets') }}
          </NuxtLink>
        </div>
      </div>
    </div>
  </section>

  <section>
    <div
      class="mt-8 lg:flex lg:flex-row lg:justify-between lg:max-w-264.5 lg:mx-auto"
    >
      <div
        class="gradients gradients-only-bottom w-76.75 h-59.5 overflow-hidden mx-auto mb-8 lg:w-76.5 lg:h-130 lg:mb-0"
      >
        <NuxtImg
          format="webp"
          :alt="t('monetizing-physical-locations')"
          class="w-76.75 h-59.5 object-cover object-top lg:w-full lg:h-full"
          src="/images/mighty-networks-alternative/monetizing-physical-locations.png"
        />
      </div>

      <div class="text-center lg:max-w-150 lg:mt-19 lg:text-left">
        <div
          class="text-2xl tracking-wide max-w-87.5 px-5 mx-auto lg:max-w-full lg:text-[1.75rem]"
        >
          <p>
            {{ t('monetizing-physical-locations') }}
            <span class="text-[#7540EE]">
              {{ t('revolutionize-access-management') }}
            </span>
          </p>
        </div>

        <div
          class="max-w-98 text-lg tracking-wide mx-auto px-5 mt-5 sm:max-w-full lg:mt-10"
        >
          <p>
            {{
              t(
                'belongs-upcoming-apple-home-integration-allows-seamless-entry-to-facilities-like-coworking-spaces-gyms-and-clubhouses-without-the-need-for-check-ins-our-system-supports-expiring-nft-memberships-based-on-membership-type-and-purchase-date'
              )
            }}
          </p>
          <p class="mt-4.5 lg:mt-5">
            {{
              t(
                'transform-your-physical-space-management-with-belongs-innovative-and-effortless-access-control'
              )
            }}
          </p>
        </div>
      </div>
    </div>
  </section>

  <section>
    <div
      class="mt-13 lg:flex lg:flex-row-reverse lg:items-start lg:justify-between lg:mx-auto lg:max-w-263 lg:mt-16.5 lg:px-5"
    >
      <NuxtImg
        :alt="t('phygital-goods')"
        class="h-61 w-full max-w-82 mx-auto object-cover lg:w-99 lg:h-73.5 lg:max-w-full lg:mx-0 lg:mt-8"
        src="/images/mighty-networks-alternative/phygital-goods.svg"
      />

      <div class="text-center lg:max-w-144.5 lg:text-left">
        <div
          class="text-2xl tracking-wide max-w-90 px-5 mx-auto lg:max-w-full lg:px-0 lg:text-[1.75rem]"
        >
          <p>
            {{ t('phygital-goods') }}
            <span>
              {{ t('leverage-nfts-for-community-merchandise') }}
            </span>
          </p>
        </div>

        <div
          class="max-w-98 text-lg tracking-wide mx-auto px-5 mt-5 sm:max-w-full lg:px-0 lg:mt-12"
        >
          <p>
            {{
              t(
                'with-belong-you-can-sell-community-swag-through-nfts-when-a-user-buys-the-nft-they-can-claim-the-physical-item-from-you-allowing-you-to-receive-payments-for-physical-goods-claimable-by-the-nft-this-novel-approach-bridges-the-gap-between-digital-and-physical-worlds-enhancing-your-communitys-engagement-and-monetization-possibilities'
              )
            }}
          </p>
          <p class="mt-4.5 lg:mt-5.5">
            {{
              t(
                'start-selling-your-unique-community-merchandise-through-nfts-and-expand-your-revenue-streams'
              )
            }}
          </p>
        </div>
      </div>
    </div>
  </section>

  <section>
    <StartBuilding class="mt-12.5" />
  </section>

  <section class="lg:hidden">
    <JoinTheRevolution class="mt-16.5 px-5" />
  </section>
</template>

<style>
@reference "tailwindcss";
.gradients {
  @apply relative;
}

.gradients:before {
  @apply absolute w-full h-12 top-0;
  background: linear-gradient(180deg, #151515 0%, rgba(21, 21, 21, 0) 100%);
  content: '';
}

.gradients:after {
  @apply absolute w-full h-12 bottom-0;
  background: linear-gradient(0deg, #151515 0%, rgba(21, 21, 21, 0) 100%);
  content: '';
}

.gradients-only-bottom::before {
  @apply hidden;
}

.gradients {
  @apply lg:flex;
}

.gradients:before,
.gradients:after {
  @apply lg:hidden;
}
</style>
